/**
 * Job-file-scoped view of the AI document-intake queue, plus the shipments-level
 * un-filed inbox.
 *
 * `useShipmentDocIntakeItems` lists the document-intake items proposed *about* one
 * shipment (its uploaded documents awaiting human approval). It calls the
 * shipments-owned, ReBAC-gated route `GET /api/v1/shipments/{id}/documents/intake-items`
 * — NOT the ungated shared `/ai-intake/items`, so a user denied access to the shipment
 * cannot read its extracted document content. This surfaces the F3 Docs Agent review on
 * the job file itself, rather than only in the global admin queue.
 *
 * The rest is the **un-filed inbox** (magic-drop child C): a document that arrived with
 * no target job. The backend classifies it, matches it to a shipment by its extracted
 * references, and either files it (it then appears in that job's review summary above)
 * or leaves it un-filed — the quarantine queue, where an operator picks the shipment.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost } from '../../../shared/api/axiosInstance';
import type { AiIntakeItem } from '../../../generated/api/models/aiIntakeItem';
import { shipmentsKeys } from './useShipmentsApi';

const SHIPMENTS_API = '/api/v1/shipments';

export const shipmentDocIntakeKey = (shipmentId: string) =>
  ['shipments', 'docIntake', shipmentId] as const;

export const documentInboxKey = ['shipments', 'documentInbox'] as const;

/** Proposed (not yet approved/rejected) file-intake items for this shipment. */
export function useShipmentDocIntakeItems(shipmentId: string) {
  return useQuery({
    queryKey: shipmentDocIntakeKey(shipmentId),
    queryFn: () =>
      apiGet<{ items: AiIntakeItem[] }>(
        `${SHIPMENTS_API}/${shipmentId}/documents/intake-items?status=proposed`,
      ).then((r) => r.items),
    enabled: Boolean(shipmentId),
  });
}

/**
 * The quarantine queue: extracted documents that could not be matched to a job.
 * Tenant-scoped + company-scoped; open to every persona — an importer receives
 * B/Ls and arrival notices it must file exactly as a forwarder does.
 */
export function useUnfiledDocIntakeItems() {
  return useQuery({
    queryKey: documentInboxKey,
    queryFn: () =>
      apiGet<{ items: AiIntakeItem[] }>(
        `${SHIPMENTS_API}/document-inbox/items?status=proposed`,
      ).then((r) => r.items),
  });
}

/**
 * File a quarantined item against the shipment the operator picked. It leaves the
 * inbox and becomes a normal job-scoped proposal on that shipment's review summary
 * — so both queues are invalidated.
 */
export function useAssignUnfiledDocIntakeItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, shipmentId }: { itemId: string; shipmentId: string }) =>
      apiPost<AiIntakeItem>(`${SHIPMENTS_API}/document-inbox/items/${itemId}/assign`, {
        shipment_id: shipmentId,
      }),
    onSuccess: (_result, { shipmentId }) => {
      qc.invalidateQueries({ queryKey: documentInboxKey });
      qc.invalidateQueries({ queryKey: shipmentDocIntakeKey(shipmentId) });
      qc.invalidateQueries({ queryKey: shipmentsKeys.documents(shipmentId) });
    },
  });
}
